import { IStore } from 'old-store/types';
import { createSelector } from 'reselect';
import { cloneDeep, filter, find, get, omit } from 'lodash';
import { IOtpLoginFlow, IShopReducer } from './types';
import {
  getPurchasedGalleriesIds,
  getGalleriesIds
} from 'modules/ScrShopNew/store/slices/galleries/selectors';
import { isCropValid } from 'modules/ScrShopNew/helpers/cropValidation';

export const getCollectionStore = (state) => state.collection;
export const getGalleriesSelector = createSelector(
  getCollectionStore,
  ({ galleries }) => galleries
);
export const getImagesStore = (state) => cloneDeep(state.images);
export const getImagesSelector = createSelector(getImagesStore, ({ images }) => images);
export const getImageByIDSelector = createSelector(
  getImagesSelector,
  (images) => (imageID: string) => find(images, { _id: imageID })
);
export const getImagesByIdsSelector = createSelector(
  getImagesSelector,
  (images) => (imageIds: string[]) =>
    images
      .filter((image) => imageIds.includes(image._id))
      .sort((a, b) => imageIds.indexOf(a._id) - imageIds.indexOf(b._id))
);
export const getImagesByGalleryId = createSelector(
  getImagesSelector,
  (images) => (galleryID: string) => filter(images, { gallery_id: galleryID })
);

export const getShopStore = ({ shop }: IStore) => shop;
export const getShopStoreWithoutImages = createSelector(getShopStore, (shop) =>
  omit(shop, ['groupedImages', 'groupedByGalleriesImages'])
);
export const getSelectedProductIDSelector = createSelector(
  getShopStore,
  ({ selectedProductID }) => selectedProductID
);
export const getCartSelectedProductIDSelector = createSelector(
  getShopStore,
  ({ cartSelectedProductID }) => cartSelectedProductID
);
export const getSelectedImagesIDsSelector = createSelector(
  getShopStore,
  ({ selectedImages }) => selectedImages
);
export const getIsImageSelectionByIDSelector = createSelector(
  getSelectedImagesIDsSelector,
  (imageIDS) => (imageID: string) => imageIDS.includes(imageID)
);
export const getShopOptionSelector = createSelector(getShopStore, ({ options }) => options);
export const getShopCroppedImagesSelector = createSelector(
  getShopStore,
  ({ croppedImages }) => croppedImages
);
export const getShopCroppedImageByIDSelector = createSelector(
  getShopCroppedImagesSelector,
  (croppedImages) => (imageID: string) => croppedImages[imageID]
);
export const getShopCroppedImagesByIDSelector = createSelector(
  getShopCroppedImagesSelector,
  (croppedImages) => (imageID: string) => croppedImages[imageID]
);
export const getIsShopEditMode = createSelector(getShopStore, ({ isEditMode }) => isEditMode);

export const getGalleryToggleStateSelector = createSelector(
  getShopStore,
  ({ galleriesToggleStates }) =>
    (galleryID: string) =>
      galleriesToggleStates?.[galleryID]
);

export const isShopActiveSelectorPure = (
  { shopActive, digitalShop }: any,
  { globalShopSettings: { printShopActive } }: IShopReducer
) => (shopActive || get(digitalShop, 'enabled', false)) && printShopActive;

export const isShopActiveSelector = createSelector(
  getCollectionStore,
  getShopStore,
  isShopActiveSelectorPure
);

export const loginFlowStatusSelector = createSelector(
  getShopStore,
  ({ loginFlowStatus }) => loginFlowStatus
);
export const forgotPasswordFlowStatusSelector = createSelector(
  getShopStore,
  ({ forgotPasswordFlowStatus }) => forgotPasswordFlowStatus
);
export const signupWithPasswordFlowStatusSelector = createSelector(
  getShopStore,
  ({ createAccountWithPasswordFlowStatus }) => createAccountWithPasswordFlowStatus
);
const EMPTY_OTP_LOGIN_FLOW: IOtpLoginFlow = {
  step: 'email',
  email: '',
  hasPassword: false,
  codeIsInvalid: false,
  isLoading: false
};

// The one-time-code flow only exists in the new shop slice. The OTP modals mount as soon as the
// collection says newShop, which is before App swaps in the new shop reducer, so fall back.
export const otpLoginFlowSelector = createSelector(
  getShopStore,
  (shop) => (shop as IShopReducer).otpLoginFlow ?? EMPTY_OTP_LOGIN_FLOW
);

export const getLaboratorySelector = createSelector(getShopStore, ({ laboratory }) => laboratory);
export const getAllLaboratoriesSelector = createSelector(
  getShopStore,
  ({ allLaboratories }: any) => allLaboratories
);

export const getCurrentGalleriesImages = createSelector(
  getGalleriesIds,
  getImagesSelector,
  (galleriesIds, images) => images.filter((image) => galleriesIds.includes(image.gallery_id))
);

export const getNotPurchasedImages = createSelector(
  getPurchasedGalleriesIds,
  getCurrentGalleriesImages,
  (purchasedGalleriesIds, images) =>
    images.filter((image) => !purchasedGalleriesIds.includes(image.gallery_id))
);

export const getHorizontalImages = createSelector(getCurrentGalleriesImages, (images) =>
  filter(images, (image) => image.width > image.height && image.extension.toLowerCase() !== 'gif')
);

export const getVerticalImages = createSelector(getCurrentGalleriesImages, (images) =>
  filter(images, (image) => image.width < image.height && image.extension.toLowerCase() !== 'gif')
);

export const getSquareImages = createSelector(getCurrentGalleriesImages, (images) =>
  filter(images, (image) => image.width === image.height && image.extension.toLowerCase() !== 'gif')
);

export const getProductRectList = createSelector(
  getShopStore,
  ({ productRecList }) => productRecList
);
export const getProductBestsellerList = createSelector(
  getShopStore,
  ({ productBestsellerList }) => productBestsellerList
);

export const getShopCroppedImagesByGroupKeySelector = createSelector(
  getShopCroppedImagesSelector,
  (croppedImages) => (groupKey: string) => {
    const filteredImages = {};

    Object.keys(croppedImages).forEach((key) => {
      if (key.startsWith(groupKey)) {
        filteredImages[key] = croppedImages[key];
      }
    });

    return filteredImages;
  }
);

// Cache for parametric selectors to prevent unnecessary recalculations
const validCroppedImagesCache = new Map();

export const getShopValidCroppedImagesByGroupKeySelector = createSelector(
  [getShopCroppedImagesSelector, (_state, groupKey: string) => groupKey],
  (croppedImages, groupKey) => {
    const cacheKey = groupKey;
    const lastResult = validCroppedImagesCache.get(cacheKey);

    // Filter and validate images
    const validImages: any[] = [];
    const keys = Object.keys(croppedImages);

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key.startsWith(groupKey)) {
        const cropData = croppedImages[key];
        if (isCropValid(cropData)) {
          validImages.push(cropData);
        }
      }
    }

    const validCount = validImages.length;

    // Return cached result if the count hasn't changed (optimization for most common case)
    if (lastResult && lastResult.validCount === validCount) {
      return lastResult;
    }

    const result = {
      validImages,
      validCount
    };

    validCroppedImagesCache.set(cacheKey, result);

    return result;
  }
);

export const getPaymentMethod = createSelector(
  getShopStore,
  (shopStore) => shopStore.paymentMethod
);
